import React, { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle } from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'

const ResetPasswordPage: React.FC = () => {
    const { token } = useParams<{ token: string }>()
    const navigate = useNavigate()
    const { showError } = useSnackbarContext()

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    })
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isValidToken, setIsValidToken] = useState<boolean | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)

    // Validate token on mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setIsValidToken(false)
                return
            }

            try {
                // Simulate API call to validate token
                await new Promise(resolve => setTimeout(resolve, 1000))

                // TODO: Implement actual token validation API call
                console.log('Validating reset token:', token)

                // Mock validation - assume token is valid
                setIsValidToken(true)

            } catch (error) {
                console.error('Failed to validate token:', error)
                setIsValidToken(false)
            }
        }

        validateToken()
    }, [token])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const validatePassword = (password: string): string[] => {
        const errors: string[] = []

        if (password.length < 8) {
            errors.push('Must be at least 8 characters long')
        }
        if (!/[A-Z]/.test(password)) {
            errors.push('Must contain at least one uppercase letter')
        }
        if (!/[a-z]/.test(password)) {
            errors.push('Must contain at least one lowercase letter')
        }
        if (!/\d/.test(password)) {
            errors.push('Must contain at least one number')
        }
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
            errors.push('Must contain at least one special character')
        }

        return errors
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const { password, confirmPassword } = formData

        // Validation
        if (!password.trim() || !confirmPassword.trim()) {
            showError('Please fill in all fields')
            return
        }

        const passwordErrors = validatePassword(password)
        if (passwordErrors.length > 0) {
            showError('Password requirements not met')
            return
        }

        if (password !== confirmPassword) {
            showError('Passwords do not match')
            return
        }

        setIsSubmitting(true)

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 2000))

            // TODO: Implement actual password reset API call
            console.log('Reset password with token:', token, 'New password:', password)

            setIsSuccess(true)

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login', {
                    state: {
                        message: 'Password reset successfully. Please sign in with your new password.'
                    }
                })
            }, 3000)

        } catch (error) {
            console.error('Failed to reset password:', error)
            showError('Failed to reset password. Please try again or request a new reset link.')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Loading state while validating token
    if (isValidToken === null) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <LoadingSpinner size="xl" />
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Validating reset link...</p>
                </div>
            </div>
        )
    }

    // Invalid token state
    if (!isValidToken) {
        return (
            <>
                <Helmet>
                    <title>Invalid Reset Link - SideGO</title>
                    <meta name="description" content="This password reset link is invalid or has expired" />
                </Helmet>

                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                    <div className="sm:mx-auto sm:w-full sm:max-w-md">
                        <div className="text-center">
                            <Link to="/" className="inline-block">
                                <h1 className="text-4xl font-bold text-primary-600 dark:text-primary-400">
                                    SideGO
                                </h1>
                            </Link>
                        </div>
                    </div>

                    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 dark:bg-red-900 mb-6">
                                    <FiAlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                                </div>

                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Invalid Reset Link
                                </h3>

                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                    This password reset link is invalid or has expired. Please request a new one.
                                </p>

                                <div className="space-y-3">
                                    <Link
                                        to="/forgot-password"
                                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                    >
                                        Request New Reset Link
                                    </Link>

                                    <Link
                                        to="/login"
                                        className="w-full flex justify-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                    >
                                        Back to Sign In
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    const passwordErrors = validatePassword(formData.password)
    const passwordsMatch = formData.password === formData.confirmPassword

    return (
        <>
            <Helmet>
                <title>Reset Password - SideGO</title>
                <meta name="description" content="Create a new password for your SideGO account" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="text-center">
                        <Link to="/" className="inline-block">
                            <h1 className="text-4xl font-bold text-primary-600 dark:text-primary-400">
                                SideGO
                            </h1>
                        </Link>
                        <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                            Reset your password
                        </h2>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {isSuccess
                                ? "Your password has been reset successfully!"
                                : "Choose a strong password to secure your account"
                            }
                        </p>
                    </div>
                </div>

                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
                        {isSuccess ? (
                            // Success State
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900 mb-6">
                                    <FiCheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                                </div>

                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Password Reset Complete
                                </h3>

                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                    Your password has been successfully reset. You can now sign in with your new password.
                                </p>

                                <p className="text-xs text-gray-500 dark:text-gray-500 mb-6">
                                    Redirecting to sign in page in 3 seconds...
                                </p>

                                <Link
                                    to="/login"
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                >
                                    Continue to Sign In
                                </Link>
                            </div>
                        ) : (
                            // Form State
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* New Password */}
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        New password
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiLock className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            id="password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            required
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                            placeholder="Enter your new password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        >
                                            {showPassword ? (
                                                <FiEyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                            ) : (
                                                <FiEye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                            )}
                                        </button>
                                    </div>

                                    {/* Password Requirements */}
                                    {formData.password && (
                                        <div className="mt-2 text-xs">
                                            <p className="text-gray-600 dark:text-gray-400 mb-1">Password requirements:</p>
                                            <ul className="space-y-1">
                                                {[
                                                    'At least 8 characters long',
                                                    'One uppercase letter',
                                                    'One lowercase letter',
                                                    'One number',
                                                    'One special character'
                                                ].map((requirement, index) => {
                                                    const isMet = passwordErrors.length === 0 || !passwordErrors.some(error =>
                                                        error.includes(requirement.toLowerCase().split(' ').slice(-2).join(' '))
                                                    )

                                                    return (
                                                        <li key={index} className={`flex items-center ${isMet ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                                                            }`}>
                                                            <span className="mr-2">{isMet ? '✓' : '×'}</span>
                                                            {requirement}
                                                        </li>
                                                    )
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Confirm new password
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiLock className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            required
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                            placeholder="Confirm your new password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        >
                                            {showConfirmPassword ? (
                                                <FiEyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                            ) : (
                                                <FiEye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                            )}
                                        </button>
                                    </div>

                                    {/* Password Match Indicator */}
                                    {formData.confirmPassword && (
                                        <div className="mt-1 text-xs">
                                            <p className={passwordsMatch ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                                                {passwordsMatch ? '✓ Passwords match' : '× Passwords do not match'}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || passwordErrors.length > 0 || !passwordsMatch || !formData.password || !formData.confirmPassword}
                                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <LoadingSpinner size="sm" className="mr-2" />
                                                Resetting password...
                                            </>
                                        ) : (
                                            'Reset password'
                                        )}
                                    </button>
                                </div>

                                <div className="text-center">
                                    <Link
                                        to="/login"
                                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
                                    >
                                        Back to sign in
                                    </Link>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default ResetPasswordPage